import { useState } from 'react';

import { prefixStorageKey } from '../string-utils';
import { clearEphemeralKeys, storeValue, formatKey, getValue, clearValue } from './utils/storage';

export const STORE_PREFIX = prefixStorageKey('useSessionStorage');

// Run the clearEphemeralKeys function upon parsing this file.
clearEphemeralKeys(sessionStorage);

/**
 * Hook for using sessionStorage in a consistent way.
 *
 * If the ephemeral flag is set, that becomes part of the used key,
 * for example "futrli.sessionStorage::ephermeral::yourKeyName" and any
 * key in sessionStorage containing "ephemeral" is cleared on page
 * load.
 *
 * @example
 *  const [value, setValue, clear] = useSessionStorage<string>('example', 'value');
 *
 * @param {string} key
 * @param {T} initialValue
 * @param {boolean} ephemeral
 * @throws {Error}
 * @returns {[T, setFunction, clearFunction]}
 */
export function useSessionStorage<T>(
  key: string,
  initialValue: T,
  ephemeral = false
): [T, (value: T) => void, () => void] {
  if (!key) {
    throw new Error(`No key provided to "${STORE_PREFIX}".`);
  }

  if (initialValue === undefined) {
    throw new Error(`No initialValue given for "${STORE_PREFIX}" with key "${key}".`);
  }

  const formattedKey = formatKey(STORE_PREFIX, key, ephemeral);
  const [state, setStateAction] = useState<T>(getValue<T>(formattedKey, initialValue, sessionStorage) as () => T);
  const store = storeValue<T>(formattedKey, setStateAction , sessionStorage);
  const clear = clearValue(formattedKey, sessionStorage);

  return [state, store, clear];
}
